import { useState, useRef, useCallback, useEffect } from 'react'
import { Trash2, Upload, AlertTriangle, CheckCircle, XCircle, Square, Loader2, BookmarkPlus, ChevronDown } from 'lucide-react'
import { Card, CardHeader, CardTitle, CardContent } from '@components/ui/Card'
import { Button } from '@components/ui/Button'
import { Input } from '@components/ui/Input'
import { apiRequest } from '@api/client'

type ApiVersion = 'v1' | 'v2'
type Environment = 'uat' | 'production'

interface ParsedEntity {
  id: string
  type: string // INDIVIDUAL | ORGANISATION
}

interface DeleteResult {
  entityId: string
  success: boolean
  skipped?: boolean
  error?: string
  relatedDeleted?: number
  relatedFailed?: number
}

interface AccountPreset {
  id: string
  name: string
  customerId: string
  apiKey: string
  childId: string
  environment: Environment
  apiVersion: ApiVersion
}

const DELETE_PROXY_URL = '/api/entity-delete'
const TREE_PROXY_URL = '/api/entity-tree'

/** Parse a single CSV line into fields, handling quoted values and escaped quotes. */
function parseCSVLine(line: string): string[] {
  const fields: string[] = []
  let current = ''
  let inQuotes = false

  for (let i = 0; i < line.length; i++) {
    const ch = line[i]
    if (inQuotes) {
      if (ch === '"') {
        if (i + 1 < line.length && line[i + 1] === '"') {
          current += '"'
          i++ // skip escaped quote
        } else {
          inQuotes = false
        }
      } else {
        current += ch
      }
    } else if (ch === '"') {
      inQuotes = true
    } else if (ch === ',') {
      fields.push(current.trim())
      current = ''
    } else {
      current += ch
    }
  }
  fields.push(current.trim())
  return fields
}

function parseCSV(text: string): ParsedEntity[] {
  const lines = text.split(/\r?\n/).filter((l) => l.trim())
  if (lines.length < 2) return []

  const headers = parseCSVLine(lines[0])

  // V1 portal exports "Entity ID" (with space), V2 exports "entityId" (camelCase)
  const entityIdIdx = headers.findIndex((h) => {
    const lower = h.toLowerCase().replace(/[\s_]/g, '')
    return lower === 'entityid'
  })
  // V1 has "Entity Type", V2 doesn't — but V2 has "latestWorkflow" which contains "Organization"
  const entityTypeIdx = headers.findIndex((h) => {
    const lower = h.toLowerCase().replace(/[\s_]/g, '')
    return lower === 'entitytype'
  })
  const latestWorkflowIdx = headers.findIndex((h) => {
    const lower = h.toLowerCase().replace(/[\s_]/g, '')
    return lower === 'latestworkflow'
  })

  if (entityIdIdx === -1) return []

  const entities: ParsedEntity[] = []
  const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i

  for (let i = 1; i < lines.length; i++) {
    const cols = parseCSVLine(lines[i])
    const id = cols[entityIdIdx] || ''
    let type = 'UNKNOWN'
    if (entityTypeIdx >= 0) {
      type = cols[entityTypeIdx] || 'UNKNOWN'
    } else if (latestWorkflowIdx >= 0) {
      // V2 CSV: infer entity type from workflow name (e.g. "AUS-Organization-Ownership")
      const workflow = (cols[latestWorkflowIdx] || '').toLowerCase()
      type = workflow.includes('organ') ? 'ORGANISATION' : 'INDIVIDUAL'
    }
    if (uuidRegex.test(id)) {
      entities.push({ id, type })
    }
  }

  return entities
}

export function DeleteEntityTool() {
  // Presets
  const [presets, setPresets] = useState<AccountPreset[]>([])
  const [showPresetDropdown, setShowPresetDropdown] = useState(false)
  const [presetName, setPresetName] = useState('')
  const [showSavePreset, setShowSavePreset] = useState(false)
  const [confirmDeletePresetId, setConfirmDeletePresetId] = useState<string | null>(null)

  // Load presets from DB on mount
  useEffect(() => {
    apiRequest<{ presets: AccountPreset[] }>('/entity-delete/presets').then((res) => {
      if (res.success && res.data?.presets) {
        setPresets(res.data.presets)
      }
    })
  }, [])

  // Form state
  const [apiVersion, setApiVersion] = useState<ApiVersion>('v1')
  const [environment, setEnvironment] = useState<Environment>('uat')
  const [customerId, setCustomerId] = useState('')
  const [childId, setChildId] = useState('')
  const [apiKey, setApiKey] = useState('')
  const [inputMode, setInputMode] = useState<'csv' | 'paste'>('csv')
  const [pasteText, setPasteText] = useState('')
  const [fileName, setFileName] = useState('')
  const [entities, setEntities] = useState<ParsedEntity[]>([])
  const [parseError, setParseError] = useState('')

  // Execution state
  const [isRunning, setIsRunning] = useState(false)
  const [progress, setProgress] = useState(0)
  const [totalToDelete, setTotalToDelete] = useState(0)
  const [results, setResults] = useState<DeleteResult[]>([])
  const [showConfirm, setShowConfirm] = useState(false)
  const [authError, setAuthError] = useState<string | null>(null)
  const abortRef = useRef(false)
  const fileInputRef = useRef<HTMLInputElement>(null)

  const applyPreset = (preset: AccountPreset) => {
    setCustomerId(preset.customerId)
    setApiKey(preset.apiKey)
    setChildId(preset.childId)
    setEnvironment(preset.environment)
    setApiVersion(preset.apiVersion)
    setShowPresetDropdown(false)
  }

  const savePreset = async () => {
    if (!presetName.trim() || !customerId.trim() || !apiKey.trim()) return
    const res = await apiRequest<{ preset: AccountPreset }>('/entity-delete/presets', {
      method: 'POST',
      body: JSON.stringify({
        name: presetName.trim(),
        customerId: customerId.trim(),
        apiKey: apiKey.trim(),
        childId: childId.trim(),
        environment,
        apiVersion,
      }),
    })
    if (res.success && res.data?.preset) {
      setPresets((prev) => [...prev, res.data!.preset])
    }
    setPresetName('')
    setShowSavePreset(false)
  }

  const deletePreset = async (id: string) => {
    await apiRequest(`/entity-delete/presets/${id}`, { method: 'DELETE' })
    setPresets((prev) => prev.filter((p) => p.id !== id))
    setConfirmDeletePresetId(null)
  }

  const individualCount = entities.filter((e) => e.type === 'INDIVIDUAL').length
  const orgCount = entities.filter((e) => e.type === 'ORGANISATION').length
  const successCount = results.filter((r) => r.success && !r.skipped).length
  const skippedCount = results.filter((r) => r.skipped).length
  const failedCount = results.filter((r) => !r.success).length
  const totalRelatedDeleted = results.reduce((sum, r) => sum + (r.relatedDeleted ?? 0), 0)
  const totalRelatedFailed = results.reduce((sum, r) => sum + (r.relatedFailed ?? 0), 0)
  const isFormValid = customerId.trim() && apiKey.trim() && entities.length > 0

  const handleFileUpload = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return

    setFileName(file.name)
    setParseError('')
    setResults([])

    const reader = new FileReader()
    reader.onload = (evt) => {
      const text = evt.target?.result as string
      const parsed = parseCSV(text)
      if (parsed.length === 0) {
        setParseError('No valid Entity IDs found in CSV. Expected a column named "Entity ID" (V1) or "entityId" (V2) with UUID values.')
        setEntities([])
      } else {
        setEntities(parsed)
      }
    }
    reader.onerror = () => {
      setParseError('Failed to read file')
      setEntities([])
    }
    reader.readAsText(file)
  }, [])

  const handlePasteChange = useCallback((text: string) => {
    setPasteText(text)
    setParseError('')
    setResults([])
    const uuidRegex = /[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/gi
    const matches = text.match(uuidRegex) || []
    const unique = [...new Set(matches.map((id) => id.toLowerCase()))]
    setEntities(unique.map((id) => ({ id, type: 'UNKNOWN' })))
  }, [])

  const doDeleteRequest = async (entity: { id: string; type?: string }) => {
    const res = await apiRequest<{
      success: boolean
      error?: string
      skipped?: boolean
      relatedDeleted?: number
      relatedFailed?: number
    }>(
      DELETE_PROXY_URL.replace('/api', ''),
      {
        method: 'POST',
        body: JSON.stringify({
          entityId: entity.id,
          entityType: entity.type,
          apiVersion,
          environment,
          apiKey: apiKey.trim(),
          customerId: customerId.trim(),
          childId: childId.trim() || undefined,
        }),
      }
    )
    if (res.authError) {
      return { httpStatus: 401, result: { success: false, error: 'Authentication failed — please sign in again.' } }
    }
    const httpStatus = res.success ? 200 : 400
    return { httpStatus, result: res.data ?? { success: res.success, error: res.error } }
  }

  const resolveOrgTree = async (entityId: string): Promise<string[] | null> => {
    try {
      const res = await apiRequest<{ success: boolean; entities?: string[] }>(
        TREE_PROXY_URL.replace('/api', ''),
        {
          method: 'POST',
          body: JSON.stringify({
            entityId,
            apiVersion,
            environment,
            apiKey: apiKey.trim(),
            customerId: customerId.trim(),
            childId: childId.trim() || undefined,
          }),
        }
      )
      if (res.success && res.data?.entities) return res.data.entities
    } catch { /* fall through */ }
    return null
  }

  const startDelete = useCallback(async () => {
    setShowConfirm(false)
    setIsRunning(true)
    setProgress(0)
    setResults([])
    setAuthError(null)
    abortRef.current = false

    // Expand orgs into their full tree (descendants + root) so each
    // entity is deleted individually in its own worker invocation,
    // avoiding Cloudflare subrequest limits.
    const expandedEntities: ParsedEntity[] = []
    for (const entity of entities) {
      const isOrg = entity.type === 'ORGANISATION' || entity.type === 'ORGANIZATION'
      if (isOrg) {
        const tree = await resolveOrgTree(entity.id)
        if (tree && tree.length > 0) {
          for (const id of tree) {
            expandedEntities.push({ id, type: id === entity.id ? 'ORGANISATION' : 'UNKNOWN' })
          }
          continue
        }
      }
      expandedEntities.push(entity)
    }

    setTotalToDelete(expandedEntities.length)
    const newResults: DeleteResult[] = []
    let consecutiveFailures = 0

    for (let i = 0; i < expandedEntities.length; i++) {
      if (abortRef.current) break

      const entity = expandedEntities[i]

      try {
        const { httpStatus, result } = await doDeleteRequest(entity)

        // Auth failure — abort immediately, don't pretend we're deleting
        if (httpStatus === 401) {
          newResults.push({
            entityId: entity.id,
            success: false,
            error: result.error || 'Authentication failed',
          })
          setProgress(i + 1)
          setResults([...newResults])
          setAuthError('Authentication failed — please sign in and try again.')
          break
        }

        if (result.success) {
          newResults.push({
            entityId: entity.id,
            success: true,
            skipped: !!result.skipped,
          })
          consecutiveFailures = 0
        } else {
          newResults.push({
            entityId: entity.id,
            success: false,
            error: result.error || `${httpStatus}`,
          })
          consecutiveFailures++
        }
      } catch (err) {
        newResults.push({
          entityId: entity.id,
          success: false,
          error: err instanceof Error ? err.message : 'Network error',
        })
        consecutiveFailures++
      }

      setProgress(i + 1)
      setResults([...newResults])

      if (consecutiveFailures >= 2) {
        break
      }

      // Small delay to avoid rate limits
      if (i < expandedEntities.length - 1 && !abortRef.current) {
        await new Promise((r) => setTimeout(r, 200))
      }
    }

    setIsRunning(false)
  }, [entities, environment, apiVersion, apiKey, customerId, childId])

  const handleStop = useCallback(() => {
    abortRef.current = true
  }, [])

  const handleReset = useCallback(() => {
    setResults([])
    setProgress(0)
    setTotalToDelete(0)
    setIsRunning(false)
    setAuthError(null)
    abortRef.current = false
  }, [])

  const failedResults = results.filter((r) => !r.success)
  const isDone = !isRunning && results.length > 0

  return (
    <Card variant="bordered">
      <CardHeader>
        <CardTitle>
          <div className="flex items-center gap-2">
            <Trash2 size={18} />
            Entity Delete Tool
          </div>
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="space-y-5">
          <p className="text-sm text-[var(--text-muted)] -mt-1">
            Bulk-delete entities from a FrankieOne environment. Upload a CSV exported from the portal
            (V1 &quot;Entity ID&quot; or V2 &quot;entityId&quot; column), enter your credentials, and the tool will
            sequentially call the delete API for each entity. Supports both V1 and V2 APIs and CSV formats.
          </p>

          {/* API Version & Environment */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-[var(--text-primary)] mb-2">
                API Version
              </label>
              <div className="flex gap-3">
                {(['v1', 'v2'] as ApiVersion[]).map((v) => (
                  <label
                    key={v}
                    className={`flex items-center gap-2 px-3 py-2 rounded-lg border cursor-pointer transition-colors ${
                      apiVersion === v
                        ? 'border-[var(--accent-color)] bg-[var(--accent-color)]/10 text-[var(--accent-color)]'
                        : 'border-[var(--border-color)] text-[var(--text-secondary)] hover:border-[var(--text-muted)]'
                    }`}
                  >
                    <input
                      type="radio"
                      name="apiVersion"
                      value={v}
                      checked={apiVersion === v}
                      onChange={() => setApiVersion(v)}
                      className="sr-only"
                      disabled={isRunning}
                    />
                    <span className="text-sm font-medium uppercase">{v}</span>
                  </label>
                ))}
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-[var(--text-primary)] mb-2">
                Environment
              </label>
              <div className="flex gap-3">
                {([
                  { value: 'uat' as Environment, label: 'UAT' },
                  { value: 'production' as Environment, label: 'Production' },
                ]).map(({ value, label }) => (
                  <label
                    key={value}
                    className={`flex items-center gap-2 px-3 py-2 rounded-lg border cursor-pointer transition-colors ${
                      environment === value
                        ? value === 'production'
                          ? 'border-red-500 bg-red-500/10 text-red-400'
                          : 'border-[var(--accent-color)] bg-[var(--accent-color)]/10 text-[var(--accent-color)]'
                        : 'border-[var(--border-color)] text-[var(--text-secondary)] hover:border-[var(--text-muted)]'
                    }`}
                  >
                    <input
                      type="radio"
                      name="environment"
                      value={value}
                      checked={environment === value}
                      onChange={() => setEnvironment(value)}
                      className="sr-only"
                      disabled={isRunning}
                    />
                    <span className="text-sm font-medium">{label}</span>
                  </label>
                ))}
              </div>
            </div>
          </div>

          {/* Production Warning */}
          {environment === 'production' && (
            <div className="flex items-start gap-2 p-3 rounded-lg bg-red-500/10 border border-red-500/30">
              <AlertTriangle size={16} className="text-red-400 shrink-0 mt-0.5" />
              <p className="text-sm text-red-400">
                <strong>Production environment selected.</strong> Entities deleted in production cannot be recovered.
              </p>
            </div>
          )}

          {/* Saved Accounts */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium text-[var(--text-primary)]">Saved Accounts</span>
              <button
                type="button"
                className="flex items-center gap-1 text-xs text-[var(--accent-color)] hover:opacity-80"
                onClick={() => setShowSavePreset((v) => !v)}
                disabled={isRunning}
              >
                <BookmarkPlus size={13} />
                Save current
              </button>
            </div>

            {/* Save preset inline form */}
            {showSavePreset && (
              <div className="flex gap-2">
                <input
                  type="text"
                  className="flex-1 px-3 py-1.5 text-sm rounded-lg border border-[var(--border-color)] bg-[var(--bg-secondary)] text-[var(--text-primary)] focus:outline-none focus:border-[var(--accent-color)]"
                  placeholder="Account name, e.g. Sales Demo UAT"
                  value={presetName}
                  onChange={(e) => setPresetName(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') savePreset() }}
                  autoFocus
                />
                <button
                  type="button"
                  className="px-3 py-1.5 text-xs font-semibold rounded-lg bg-[var(--accent-color)] text-white disabled:opacity-50"
                  onClick={savePreset}
                  disabled={!presetName.trim() || !customerId.trim() || !apiKey.trim()}
                >
                  Save
                </button>
                <button
                  type="button"
                  className="px-3 py-1.5 text-xs rounded-lg border border-[var(--border-color)] text-[var(--text-secondary)]"
                  onClick={() => setShowSavePreset(false)}
                >
                  Cancel
                </button>
              </div>
            )}

            {/* Preset list / dropdown */}
            {presets.length > 0 ? (
              <div className="relative">
                <button
                  type="button"
                  className="w-full flex items-center justify-between px-3 py-2 rounded-lg border border-[var(--border-color)] bg-[var(--bg-secondary)] text-sm text-[var(--text-primary)] hover:border-[var(--accent-color)] disabled:opacity-50"
                  onClick={() => setShowPresetDropdown((v) => !v)}
                  disabled={isRunning}
                >
                  <span className="text-[var(--text-secondary)]">Select a saved account…</span>
                  <ChevronDown size={14} />
                </button>
                {showPresetDropdown && (
                  <div className="absolute top-full left-0 right-0 mt-1 bg-[var(--bg-secondary)] border border-[var(--border-color)] rounded-lg shadow-lg z-20 overflow-hidden">
                    {presets.map((preset) => (
                      <div key={preset.id} className="flex items-center justify-between px-3 py-2 hover:bg-[var(--bg-tertiary)] group">
                        {confirmDeletePresetId === preset.id ? (
                          <div className="flex items-center justify-between w-full gap-2">
                            <span className="text-xs text-red-400">Delete &quot;{preset.name}&quot;?</span>
                            <div className="flex gap-1.5 shrink-0">
                              <button
                                type="button"
                                className="px-2 py-0.5 text-xs font-medium rounded bg-red-500/20 text-red-400 hover:bg-red-500/30"
                                onClick={() => deletePreset(preset.id)}
                              >
                                Yes, delete
                              </button>
                              <button
                                type="button"
                                className="px-2 py-0.5 text-xs rounded border border-[var(--border-color)] text-[var(--text-secondary)] hover:bg-[var(--bg-secondary)]"
                                onClick={() => setConfirmDeletePresetId(null)}
                              >
                                Cancel
                              </button>
                            </div>
                          </div>
                        ) : (
                          <>
                            <button
                              type="button"
                              className="flex-1 text-left text-sm text-[var(--text-primary)]"
                              onClick={() => applyPreset(preset)}
                            >
                              <span className="font-medium">{preset.name}</span>
                              <span className="ml-2 text-xs text-[var(--text-muted)]">{preset.environment.toUpperCase()} · v{preset.apiVersion === 'v1' ? '1' : '2'}</span>
                            </button>
                            <button
                              type="button"
                              className="opacity-0 group-hover:opacity-100 text-red-400 hover:text-red-300 p-1"
                              onClick={() => setConfirmDeletePresetId(preset.id)}
                              title="Remove preset"
                            >
                              <Trash2 size={12} />
                            </button>
                          </>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ) : (
              <p className="text-xs text-[var(--text-muted)]">No saved accounts yet. Fill in credentials below then click Save current.</p>
            )}
          </div>

          {/* Credentials */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Input
              label="Customer ID"
              placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
              value={customerId}
              onChange={(e) => setCustomerId(e.target.value)}
              disabled={isRunning}
            />
            <Input
              label="Child ID"
              placeholder="Optional"
              hint="Only required for multi-tenant accounts"
              value={childId}
              onChange={(e) => setChildId(e.target.value)}
              disabled={isRunning}
            />
          </div>

          <Input
            label="API Key"
            type="password"
            placeholder="Enter your FrankieOne API key"
            value={apiKey}
            onChange={(e) => setApiKey(e.target.value)}
            disabled={isRunning}
          />

          {/* Entity Input — CSV or paste */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="text-sm font-medium text-[var(--text-primary)]">Entities to Delete</label>
              <div className="flex gap-1 p-0.5 rounded-lg bg-[var(--bg-secondary)] border border-[var(--border-color)]">
                {(['csv', 'paste'] as const).map((mode) => (
                  <button
                    key={mode}
                    type="button"
                    disabled={isRunning}
                    onClick={() => {
                      setInputMode(mode)
                      setEntities([])
                      setParseError('')
                      setFileName('')
                      setPasteText('')
                    }}
                    className={`px-3 py-1 text-xs font-medium rounded-md transition-colors ${
                      inputMode === mode
                        ? 'bg-[var(--bg-primary)] text-[var(--text-primary)] shadow-sm'
                        : 'text-[var(--text-muted)] hover:text-[var(--text-secondary)]'
                    }`}
                  >
                    {mode === 'csv' ? 'CSV Upload' : 'Paste IDs'}
                  </button>
                ))}
              </div>
            </div>

            {inputMode === 'csv' ? (
              <div
                className={`relative border-2 border-dashed rounded-lg p-6 text-center transition-colors cursor-pointer hover:border-[var(--accent-color)] ${
                  fileName
                    ? 'border-[var(--accent-color)]/50 bg-[var(--accent-color)]/5'
                    : 'border-[var(--border-color)]'
                }`}
                onClick={() => !isRunning && fileInputRef.current?.click()}
              >
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".csv"
                  onChange={handleFileUpload}
                  className="hidden"
                  disabled={isRunning}
                />
                <Upload size={24} className="mx-auto mb-2 text-[var(--text-muted)]" />
                {fileName ? (
                  <div>
                    <p className="text-sm font-medium text-[var(--text-primary)]">{fileName}</p>
                    {entities.length > 0 && (
                      <p className="text-xs text-[var(--text-muted)] mt-1">
                        Found <strong>{entities.length}</strong> entities
                        {individualCount > 0 && ` (${individualCount} individual${individualCount !== 1 ? 's' : ''}` }
                        {orgCount > 0 && `${individualCount > 0 ? ', ' : ' ('}${orgCount} organisation${orgCount !== 1 ? 's' : ''}`}
                        {(individualCount > 0 || orgCount > 0) && ')'}
                      </p>
                    )}
                  </div>
                ) : (
                  <p className="text-sm text-[var(--text-muted)]">
                    Click to upload a CSV exported from the FrankieOne portal
                  </p>
                )}
              </div>
            ) : (
              <div>
                <textarea
                  className="w-full h-28 px-3 py-2 text-sm rounded-lg border border-[var(--border-color)] bg-[var(--bg-secondary)] text-[var(--text-primary)] placeholder-[var(--text-muted)] focus:outline-none focus:border-[var(--accent-color)] resize-none font-mono"
                  placeholder="Paste one or more entity IDs, one per line&#10;e.g. 31d61177-0b2b-bb34-369a-90b0bcfa9a97"
                  value={pasteText}
                  onChange={(e) => handlePasteChange(e.target.value)}
                  disabled={isRunning}
                />
                {entities.length > 0 && (
                  <p className="text-xs text-[var(--text-muted)] mt-1">
                    Found <strong>{entities.length}</strong> valid ID{entities.length !== 1 ? 's' : ''}
                  </p>
                )}
              </div>
            )}

            {parseError && (
              <p className="text-xs text-red-400 mt-1">{parseError}</p>
            )}
          </div>

          {/* Progress Bar */}
          {(isRunning || isDone) && (
            <div className="space-y-2">
              <div className="flex items-center justify-between text-sm">
                <span className="text-[var(--text-secondary)]">
                  {isRunning ? (
                    <span className="flex items-center gap-2">
                      <Loader2 size={14} className="animate-spin" />
                      Deleting {progress} of {totalToDelete}...
                    </span>
                  ) : (
                    `Completed — ${progress} of ${totalToDelete} processed`
                  )}
                </span>
                <span className="text-[var(--text-muted)]">
                  {failedCount > 0 && (
                    <span className="text-red-400">{failedCount} failed</span>
                  )}
                </span>
              </div>
              <div className="w-full h-2 bg-[var(--bg-secondary)] rounded-full overflow-hidden">
                <div
                  className={`h-full rounded-full transition-all duration-300 ${
                    failedCount > 0 && !isRunning ? 'bg-yellow-500' : 'bg-[var(--accent-color)]'
                  }`}
                  style={{ width: `${totalToDelete > 0 ? (progress / totalToDelete) * 100 : 0}%` }}
                />
              </div>
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex gap-3">
            {!isRunning && !isDone && (
              <Button
                variant="danger"
                onClick={() => setShowConfirm(true)}
                disabled={!isFormValid}
              >
                <Trash2 size={16} />
                Delete All Entities
              </Button>
            )}

            {isRunning && (
              <Button variant="secondary" onClick={handleStop}>
                <Square size={14} />
                Stop
              </Button>
            )}

            {isDone && (
              <Button variant="secondary" onClick={handleReset}>
                Reset
              </Button>
            )}
          </div>

          {/* Auth Error Banner */}
          {authError && (
            <div className="flex items-start gap-2 p-3 rounded-lg bg-red-500/10 border border-red-500/30">
              <AlertTriangle size={16} className="text-red-400 shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-medium text-red-400">{authError}</p>
                <p className="text-xs text-red-400/70 mt-1">
                  No entities were deleted. Sign in and try again.
                </p>
              </div>
            </div>
          )}

          {/* Results Summary */}
          {isDone && !authError && (
            <div className="space-y-3 pt-2 border-t border-[var(--border-color)]">
              <h4 className="text-sm font-medium text-[var(--text-primary)]">Results</h4>
              <div className="flex gap-4">
                <span className="flex items-center gap-1.5 text-sm text-green-400">
                  <CheckCircle size={14} />
                  {successCount} deleted
                </span>
                {skippedCount > 0 && (
                  <span className="flex items-center gap-1.5 text-sm text-[var(--text-muted)]">
                    <Square size={14} />
                    {skippedCount} not found
                  </span>
                )}
                {failedCount > 0 && (
                  <span className="flex items-center gap-1.5 text-sm text-red-400">
                    <XCircle size={14} />
                    {failedCount} failed
                  </span>
                )}
                {totalRelatedDeleted > 0 && (
                  <span className="flex items-center gap-1.5 text-sm text-blue-400">
                    <CheckCircle size={14} />
                    {totalRelatedDeleted} related entit{totalRelatedDeleted === 1 ? 'y' : 'ies'} deleted
                  </span>
                )}
                {totalRelatedFailed > 0 && (
                  <span className="flex items-center gap-1.5 text-sm text-orange-400">
                    <AlertTriangle size={14} />
                    {totalRelatedFailed} related entit{totalRelatedFailed === 1 ? 'y' : 'ies'} failed
                  </span>
                )}
                {abortRef.current && progress < totalToDelete && (
                  <span className="text-sm text-[var(--text-muted)]">
                    {totalToDelete - progress} skipped (stopped)
                  </span>
                )}
              </div>

              {/* Failed Details */}
              {failedResults.length > 0 && (
                <div className="space-y-1">
                  <p className="text-xs font-medium text-[var(--text-muted)] uppercase tracking-wider">
                    Failed Entities
                  </p>
                  <div className="max-h-40 overflow-y-auto space-y-1">
                    {failedResults.map((r) => (
                      <div
                        key={r.entityId}
                        className="flex items-start gap-2 text-xs p-2 rounded bg-red-500/5 border border-red-500/20"
                      >
                        <code className="text-[var(--text-secondary)] shrink-0 font-mono">
                          {r.entityId.substring(0, 8)}...
                        </code>
                        <span className="text-red-400">{r.error}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Confirmation Dialog */}
          {showConfirm && (
            <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60">
              <div className="bg-[var(--bg-primary)] border border-[var(--border-color)] rounded-xl p-6 max-w-md mx-4 shadow-xl">
                <div className="flex items-center gap-3 mb-4">
                  <div className={`p-2 rounded-full ${environment === 'production' ? 'bg-red-500/20' : 'bg-yellow-500/20'}`}>
                    <AlertTriangle size={20} className={environment === 'production' ? 'text-red-400' : 'text-yellow-400'} />
                  </div>
                  <h3 className="text-lg font-semibold text-[var(--text-primary)]">
                    Confirm Deletion
                  </h3>
                </div>
                <p className="text-sm text-[var(--text-secondary)] mb-1">
                  You are about to delete <strong>{entities.length}</strong> entities from{' '}
                  <strong className={environment === 'production' ? 'text-red-400' : 'text-[var(--accent-color)]'}>
                    {environment === 'production' ? 'PRODUCTION' : 'UAT'}
                  </strong>
                  {' '}using the <strong>{apiVersion.toUpperCase()}</strong> API.
                </p>
                <p className="text-sm text-[var(--text-muted)] mb-5">
                  This action cannot be undone.
                </p>
                <div className="flex justify-end gap-3">
                  <Button variant="secondary" onClick={() => setShowConfirm(false)}>
                    Cancel
                  </Button>
                  <Button variant="danger" onClick={startDelete}>
                    <Trash2 size={14} />
                    Delete {entities.length} Entities
                  </Button>
                </div>
              </div>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  )
}
